import axios from 'axios';
import { auth } from './firebase';

const normalizeApiBaseUrl = (value) => {
  const fallback = 'http://localhost:8000';
  const raw = (value && value.trim()) || fallback;
  const trimmed = raw.replace(/\/+$/, '');
  if (trimmed.endsWith('/api')) {
    return trimmed;
  }
  return `${trimmed}/api`;
};

export const API_BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_API_BASE_URL || '');

export const api = axios.create({
  baseURL: API_BASE_URL,
});

const getJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
};

const isUsableToken = (token) => {
  const payload = getJwtPayload(token);
  if (!payload) return false;

  const expiresAt = typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  return !expiresAt || expiresAt > Date.now() + 60000;
};

const shouldUseInjectedToken = () => {
  if (typeof window === 'undefined') return false;
  if (window.ReactNativeWebView) return true;

  try {
    return new URLSearchParams(window.location.search).get('mode') === 'mobile';
  } catch (e) {
    return false;
  }
};

const readInjectedToken = () => {
  const rawToken = localStorage.getItem('token');
  if (!rawToken) return null;

  if (!shouldUseInjectedToken()) {
    localStorage.removeItem('token');
    console.warn('[OdysseyAI auth] Removed injected token outside mobile mode; continuing as guest.');
    return null;
  }

  let parsed = rawToken;
  try {
    parsed = JSON.parse(rawToken);
  } catch (e) {
    // Raw token string, handled below.
  }

  const token = typeof parsed === 'string'
    ? parsed
    : parsed?.token || parsed?.idToken || parsed?.accessToken || null;

  const normalized = typeof token === 'string' ? token.trim() : '';
  if (!isUsableToken(normalized)) {
    localStorage.removeItem('token');
    console.warn('[OdysseyAI auth] Ignored stale or invalid injected token for guest request.');
    return null;
  }

  return normalized;
};

const deleteHeader = (headers, name) => {
  if (!headers) return;
  if (typeof headers.delete === 'function') {
    headers.delete(name);
    return;
  }
  delete headers[name];
  delete headers[name.toLowerCase()];
};

export const buildApiHeaders = async (baseHeaders = {}) => {
  const headers = { ...baseHeaders };
  const user = auth.currentUser;
  // Select test credits only for the current native account. Server-verified
  // receipts are the sole source of those credits; this header grants nothing.
  deleteHeader(headers, 'X-Odyssey-Store');
  if (user && window.ReactNativeWebView && window.__ODYSSEY_APP_STORE__?.uid === user.uid
      && window.__ODYSSEY_APP_STORE__.environment === 'Sandbox') headers['X-Odyssey-Store'] = 'Sandbox';
  if (user) {
    const now = Date.now();
    const tokenCache = sessionStorage.getItem('authToken');
    const tokenExpiry = sessionStorage.getItem('authTokenExpiry');
    let token = tokenCache && sessionStorage.getItem('authTokenOwner') === user.uid
      && tokenExpiry && now < parseInt(tokenExpiry, 10)
      ? tokenCache
      : null;
    if (!token) {
      token = await user.getIdToken(true);
      if (auth.currentUser?.uid !== user.uid) {
        throw new axios.CanceledError('Account changed during token refresh');
      }
      const claims = getJwtPayload(token) || {};
      const newExpiry = typeof claims.exp === 'number'
        ? claims.exp * 1000 - 60000
        : now + 55 * 60 * 1000;
      sessionStorage.setItem('authToken', token);
      sessionStorage.setItem('authTokenOwner', user.uid);
      sessionStorage.setItem('authTokenExpiry', newExpiry.toString());
    }
    headers.Authorization = `Bearer ${token}`;
  } else {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authTokenOwner');
    sessionStorage.removeItem('authTokenExpiry');
    const injectedToken = readInjectedToken();
    if (injectedToken) {
      headers.Authorization = `Bearer ${injectedToken}`;
    } else {
      const anonId = localStorage.getItem('anon_id');
      if (anonId && !headers['X-Anon-Id']) headers['X-Anon-Id'] = anonId;
    }
  }
  if (headers.Authorization) deleteHeader(headers, 'X-Anon-Id');
  return headers;
};

// Invalidate token cache on auth state change (login/logout)
auth.onIdTokenChanged(() => {
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('authTokenOwner');
  sessionStorage.removeItem('authTokenExpiry');
});

api.interceptors.request.use(async (config) => {
  const requireExpectedAccount = () => {
    if (config.expectedUserId && auth.currentUser?.uid !== config.expectedUserId) {
      throw new axios.CanceledError('Account changed before request');
    }
  };
  requireExpectedAccount();
  const resolvedHeaders = await buildApiHeaders(config.headers || {});
  requireExpectedAccount();
  deleteHeader(config.headers, 'X-Odyssey-Store');
  Object.entries(resolvedHeaders).forEach(([name, value]) => {
    if (typeof config.headers?.set === 'function') config.headers.set(name, value);
    else config.headers[name] = value;
  });
  if (resolvedHeaders.Authorization) deleteHeader(config.headers, 'X-Anon-Id');
  return config;
});
